import { MOCK_PAYOUT_SEEDS } from "@/mocks/payouts";
import { listGroups } from "@/services/groups";
import { listMembers } from "@/services/members";
import { buildAllDemands } from "@/services/collections";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import {
  ApiError,
  type CollectionDemand,
  type KametiGroup,
  type Member,
  type PayoutApproval,
  type PayoutDisbursement,
  type PayoutManualDeduction,
  type PayoutSeed,
  type PayoutStatus,
  type PayoutView,
  type RecordDisbursementInput,
} from "@/services/types";
import { cyclePeriodLabel } from "@/utils/group";
import { computeCommission } from "@/utils/auction";

/**
 * Centralized payout service. Every payout screen calls through here — no screen
 * performs a direct data fetch. Payouts originate when an auction closes; this
 * service resolves each seed against the group, member and collection data so the
 * prize, dues, funding and winner details always agree with Phases 1–4.
 *
 * Approval and disbursement are separate steps. Approval is restricted to the
 * owner roles (enforced in the UI); disbursement requires prior approval. Only
 * same-group dues are adjusted against the prize; other-group balances are shown
 * for awareness but never deducted. Payout records are never deleted — only
 * reversed with a mandatory reason.
 */

const delay = (ms = 350) => new Promise((resolve) => setTimeout(resolve, ms));

/** In-memory store seeded from mocks — used only when USE_MOCK_API is true. */
let store: PayoutSeed[] = [...MOCK_PAYOUT_SEEDS];

function findSeed(id: string): PayoutSeed {
  const seed = store.find((s) => s.id === id);
  if (!seed) throw new ApiError("Payout not found.", 404, "PAYOUT_NOT_FOUND");
  return seed;
}

/* ================= Real API — backend shapes and mapping ================= */

interface BackendPayoutRow {
  id: string;
  voucherNo: string;
  cycleId: string;
  groupMemberId: string;
  prizeAmount: string;
  duesAdjusted: string;
  otherDeductions: string;
  netPayable: string;
  amountPaid: string;
  mode: string;
  referenceNo: string | null;
  paidOn: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  status: string;
  reversedAt: string | null;
  reversalReason: string | null;
  remarks: string | null;
  createdAt: string;
  cycleNo: number;
  periodLabel: string;
  commissionAmount?: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  ticketNo: number;
  memberCode: string;
  fullName: string;
}

interface BackendPayoutDetail extends BackendPayoutRow {
  kind: string;
  grossPool: string;
  discountAmount: string;
  dividendPerMember: string;
  commissionAmount?: string;
  memberId: string;
  phone: string;
  city: string | null;
  bankAccountName: string | null;
  bankIfsc: string | null;
  isOrganizer: boolean;
  approvedByName: string | null;
  reversedByName: string | null;
  deductions: { id: string; amount: string; reason: string; createdBy: string | null; createdAt: string }[];
  disbursements: { id: string; amount: string; mode: string; referenceNo: string | null; paidOn: string; disbursedBy: string | null; createdAt: string }[];
  funding: { expectedCollection: string; actualCollected: string; shortfall: string };
  sameGroupInstallments: { cycleNo: number; periodLabel: string; netPayable: string; amountPaid: string; balance: string; status: string }[];
  otherGroupBalances: { groupId: string; groupCode: string; groupName: string; outstanding: string }[];
}

interface BackendVoucher extends BackendPayoutRow {
  memberCode: string;
  fullName: string;
  phone: string;
  approvedByName: string | null;
  deductions: { id: string; amount: string; reason: string }[];
  disbursements: { id: string; amount: string; mode: string; referenceNo: string | null; paidOn: string; disbursedByName: string | null }[];
  amountInWords: string;
}

const PAYOUT_STATUS_FROM_BACKEND: Record<string, PayoutStatus> = {
  PENDING_APPROVAL: "pending_approval",
  APPROVED: "approved",
  PARTIALLY_PAID: "partially_disbursed",
  PAID: "disbursed",
  REVERSED: "reversed",
};

const PAYMENT_MODE_TO_BACKEND: Record<string, string> = { cash: "CASH", upi: "UPI", bank_transfer: "BANK_TRANSFER", cheque: "CHEQUE", member_account: "MEMBER_ACCOUNT" };
const PAYMENT_MODE_FROM_BACKEND: Record<string, string> = { CASH: "cash", UPI: "upi", BANK_TRANSFER: "bank_transfer", CHEQUE: "cheque", MEMBER_ACCOUNT: "member_account" };

/** Local "YYYY-MM-DD" — matches DatePicker.tsx / collections.ts's own fix; a string already in this shape passes through untouched. */
function toDateOnlyString(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayDateOnly(): string {
  return toDateOnlyString(new Date().toISOString());
}

function mapDeduction(d: { id: string; amount: string; reason: string; createdBy?: string | null; createdAt?: string }): PayoutManualDeduction {
  return {
    id: d.id,
    amount: Number(d.amount),
    reason: d.reason,
    addedBy: d.createdBy ?? "",
    addedAt: d.createdAt ?? "",
  };
}

function mapDisbursement(d: {
  id: string;
  amount: string;
  mode: string;
  referenceNo: string | null;
  paidOn: string;
  disbursedBy?: string | null;
  disbursedByName?: string | null;
  createdAt?: string;
}): PayoutDisbursement {
  return {
    id: d.id,
    amount: Number(d.amount),
    mode: (PAYMENT_MODE_FROM_BACKEND[d.mode] ?? "cash") as PayoutDisbursement["mode"],
    reference: d.referenceNo ?? "",
    paidOn: d.paidOn,
    remarks: "",
    disbursedBy: d.disbursedByName ?? d.disbursedBy ?? "",
    recordedAt: d.createdAt ?? d.paidOn,
  };
}

function mapPayoutDetail(row: BackendPayoutDetail): PayoutView {
  const approval: PayoutApproval | null = row.approvedBy
    ? { approvedBy: row.approvedByName ?? row.approvedBy, approvedAt: row.approvedAt ?? "", remarks: row.remarks ?? "" }
    : null;

  return {
    id: row.id,
    voucherNumber: row.voucherNo,
    groupId: row.groupId,
    groupCode: row.groupCode,
    groupName: row.groupName,
    cycle: row.cycleNo,
    periodLabel: row.periodLabel,
    isOrganizerReserved: row.kind === "ORGANIZER_RESERVED",
    winnerMemberId: row.memberId,
    winnerCode: row.memberCode,
    winnerName: row.fullName,
    winnerPhone: row.phone,
    winnerCity: row.city ?? "",
    winnerTicketNumber: String(row.ticketNo),
    isOrganizer: row.isOrganizer,
    bankAccountName: row.bankAccountName ?? "",
    ifsc: row.bankIfsc ?? "",
    pool: Number(row.grossPool),
    prize: Number(row.prizeAmount),
    commissionAmount: row.commissionAmount != null ? Number(row.commissionAmount) : 0,
    discount: Number(row.discountAmount),
    perMemberDividend: Number(row.dividendPerMember),
    expectedCollection: Number(row.funding.expectedCollection),
    collected: Number(row.funding.actualCollected),
    fundingShortfall: Number(row.funding.shortfall),
    // Itemised from the winner's currently-outstanding same-group
    // installments (see the backend's own comment on this field) —
    // sameGroupDuesTotal below is the authoritative, fixed figure actually
    // deducted at close time, not a sum of these lines: they can drift out
    // of sync with it if the winner pays down an old due afterwards.
    sameGroupDues: row.sameGroupInstallments
      .filter((i) => Number(i.balance) > 0)
      .map((i) => ({ cycle: i.cycleNo, periodLabel: i.periodLabel, amount: Number(i.balance) })),
    sameGroupDuesTotal: Number(row.duesAdjusted),
    otherGroupBalances: row.otherGroupBalances.map((b) => ({
      groupId: b.groupId,
      groupName: b.groupName,
      groupCode: b.groupCode,
      outstanding: Number(b.outstanding),
    })),
    manualDeductions: row.deductions.map(mapDeduction),
    manualDeductionsTotal: Number(row.otherDeductions),
    netPayable: Number(row.netPayable),
    amountPaid: Number(row.amountPaid),
    balanceOwed: Math.max(0, Number(row.netPayable) - Number(row.amountPaid)),
    status: PAYOUT_STATUS_FROM_BACKEND[row.status] ?? "pending_approval",
    approval,
    disbursements: row.disbursements.map(mapDisbursement),
    reversedAt: row.reversedAt,
    reversedBy: row.reversedByName,
    reversalReason: row.reversalReason,
    createdAt: row.createdAt,
  };
}

/**
 * Dashboard/history list rows don't carry the full itemised disbursement
 * history (funding, itemised dues, deductions) — fetched lazily via
 * getPayout() when a screen actually needs them. The payout row itself does
 * carry its cumulative amountPaid and the mode/reference/date of the most
 * recent disbursement (updated in place on every /disburse call), so a
 * single synthetic disbursement entry is reconstructed from those columns —
 * this is what keeps the history table's Mode column/filter and the
 * dashboard's "disbursed this month" stat working without a per-row detail
 * fetch for every payout in the list.
 */
function mapPayoutListRow(row: BackendPayoutRow): PayoutView {
  return mapPayoutDetail({
    ...row,
    kind: "AUCTION",
    grossPool: "0",
    discountAmount: "0",
    dividendPerMember: "0",
    memberId: "",
    phone: "",
    city: null,
    bankAccountName: null,
    bankIfsc: null,
    isOrganizer: false,
    approvedByName: null,
    reversedByName: null,
    deductions: [],
    disbursements:
      Number(row.amountPaid) > 0
        ? [
            {
              id: row.id,
              amount: row.amountPaid,
              mode: row.mode,
              referenceNo: row.referenceNo,
              paidOn: row.paidOn ?? row.createdAt,
              disbursedBy: null,
              createdAt: row.paidOn ?? row.createdAt,
            },
          ]
        : [],
    funding: { expectedCollection: "0", actualCollected: "0", shortfall: "0" },
    sameGroupInstallments: [],
    otherGroupBalances: [],
  });
}

async function fetchPayoutDetail(id: string): Promise<PayoutView> {
  const detail = await http<BackendPayoutDetail>(`/payouts/${id}`, { method: "GET" });
  return mapPayoutDetail(detail);
}

/* ================= Mock — pure resolution over the seeded store ================= */

/** Preloaded context shared across a batch of payout views (avoids reloading). */
interface PayoutCtx {
  groups: KametiGroup[];
  members: Member[];
  demands: CollectionDemand[] | null;
}

async function loadCtx(): Promise<PayoutCtx> {
  const [groups, members] = await Promise.all([listGroups(), listMembers()]);
  const needsDemands = store.some((s) => groups.find((g) => g.id === s.groupId)?.status === "active");
  const demands = needsDemands ? await buildAllDemands() : null;
  return { groups, members, demands };
}

/** Resolve a raw seed into a full payout view. */
async function buildPayout(seed: PayoutSeed, ctx: PayoutCtx): Promise<PayoutView> {
  const group = ctx.groups.find((g) => g.id === seed.groupId);
  if (!group) throw new ApiError("Group not found.", 404, "GROUP_NOT_FOUND");
  const won = group.wonCycles.find((w) => w.cycle === seed.cycle);
  if (!won) throw new ApiError("This cycle has not been decided yet.", 404, "CYCLE_OPEN");
  const winner = ctx.members.find((m) => m.id === won.memberId);
  const ticket = group.tickets.find((t) => t.memberId === won.memberId);
  const isReserved = seed.cycle === group.reservedCycle;

  const pool = group.chitValue;
  const prize = seed.prize;
  const discount = Math.max(0, pool - prize);
  const perMemberDividend = isReserved ? 0 : discount > 0 ? Math.floor(discount / group.memberCount) : 0;

  const sameGroupDues = ctx.demands
    ? ctx.demands
        .filter((d) => d.groupId === group.id && d.memberId === won.memberId && d.balance > 0)
        .map((d) => ({
          cycle: d.cycle,
          periodLabel: cyclePeriodLabel(group.startDate, d.cycle),
          amount: d.balance,
        }))
    : seed.sameGroupDues.map((d) => ({
        cycle: d.cycle,
        periodLabel: cyclePeriodLabel(group.startDate, d.cycle),
        amount: d.amount,
      }));
  const sameGroupDuesTotal = sameGroupDues.reduce((s, d) => s + d.amount, 0);
  const manualDeductions = seed.manualDeductions ?? [];
  const manualDeductionsTotal = manualDeductions.reduce((s, d) => s + d.amount, 0);
  const netPayable = Math.max(0, prize - sameGroupDuesTotal - manualDeductionsTotal);
  const disbursements = seed.disbursements ?? [];
  const amountPaid = disbursements.reduce((s, d) => s + d.amount, 0);
  const balanceOwed = Math.max(0, netPayable - amountPaid);

  let status: PayoutView["status"];
  if (seed.reversedAt) status = "reversed";
  else if (amountPaid >= netPayable - 0.01) status = "disbursed";
  else if (amountPaid > 0) status = "partially_disbursed";
  else if (seed.approval) status = "approved";
  else status = "pending_approval";

  const groupCodeMap = new Map(ctx.groups.map((g) => [g.id, g.groupCode]));
  const otherGroupBalances = (winner?.enrollments ?? [])
    .filter((e) => e.groupId !== group.id && e.outstanding > 0)
    .map((e) => ({
      groupId: e.groupId,
      groupName: e.groupName,
      groupCode: groupCodeMap.get(e.groupId) ?? "—",
      outstanding: e.outstanding,
    }));

  // Funding figures come from the same demand derivation as the collection
  // module, so both modules always agree. Completed groups (g12) have no active
  // demands — funding is reported as nil for their historical payouts.
  let expectedCollection = 0;
  let collected = 0;
  if (ctx.demands) {
    const cycleDemands = ctx.demands.filter((d) => d.groupId === group.id && d.cycle === seed.cycle);
    expectedCollection = cycleDemands.reduce((s, d) => s + d.netPayable, 0);
    collected = cycleDemands.reduce((s, d) => s + d.paid, 0);
  }
  const fundingShortfall = Math.max(0, netPayable - collected);

  return {
    id: seed.id,
    voucherNumber: seed.voucherNumber,
    groupId: group.id,
    groupCode: group.groupCode,
    groupName: group.name,
    cycle: seed.cycle,
    periodLabel: cyclePeriodLabel(group.startDate, seed.cycle),
    isOrganizerReserved: isReserved,
    winnerMemberId: won.memberId,
    winnerCode: winner?.memberCode ?? "—",
    winnerName: winner?.fullName ?? "—",
    winnerPhone: winner?.primaryPhone ?? "—",
    winnerCity: winner?.city ?? "—",
    winnerTicketNumber: ticket?.ticketNumber ?? "—",
    isOrganizer: won.memberId === group.organizerMemberId,
    bankAccountName: winner?.bankAccountName ?? "",
    ifsc: winner?.ifsc ?? "",
    pool,
    prize,
    commissionAmount: isReserved
      ? (group.commissionType !== "none" ? computeCommission(group.commissionType, group.commissionValue, pool, 0) : 0)
      : computeCommission(group.commissionType, group.commissionValue, pool, discount),
    discount,
    perMemberDividend,
    expectedCollection: Math.round(expectedCollection * 100) / 100,
    collected: Math.round(collected * 100) / 100,
    fundingShortfall: Math.round(fundingShortfall * 100) / 100,
    sameGroupDues,
    sameGroupDuesTotal: Math.round(sameGroupDuesTotal * 100) / 100,
    otherGroupBalances,
    manualDeductions,
    manualDeductionsTotal: Math.round(manualDeductionsTotal * 100) / 100,
    netPayable: Math.round(netPayable * 100) / 100,
    amountPaid: Math.round(amountPaid * 100) / 100,
    balanceOwed: Math.round(balanceOwed * 100) / 100,
    status,
    approval: seed.approval ?? null,
    disbursements,
    reversedAt: seed.reversedAt ?? null,
    reversedBy: seed.reversedBy ?? null,
    reversalReason: seed.reversalReason ?? null,
    createdAt: seed.createdAt,
  };
}

/* ================= Exported service ================= */

export interface PayoutQuery {
  groupId?: string;
  status?: string;
  mode?: string;
}

/** Every payout across all groups, newest first. */
export async function listPayouts(query: PayoutQuery = {}): Promise<PayoutView[]> {
  if (!USE_MOCK_API) {
    const params = new URLSearchParams({ pageSize: "200" });
    if (query.groupId) params.set("groupId", query.groupId);
    if (query.status) {
      const backendStatus = Object.entries(PAYOUT_STATUS_FROM_BACKEND).find(([, v]) => v === query.status)?.[0];
      if (backendStatus) params.set("status", backendStatus);
    }
    const res = await http<{ data: BackendPayoutRow[] }>(`/payouts?${params.toString()}`, { method: "GET", raw: true });
    let out = res.data.map(mapPayoutListRow).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    // mode isn't a /payouts filter on the backend (it lives on individual
    // disbursements, which list rows don't carry) — applied client-side
    // against whichever mode the payout was last disbursed under.
    if (query.mode) out = out.filter((p) => p.disbursements.some((d) => d.mode === query.mode));
    return out;
  }

  await delay();
  const ctx = await loadCtx();
  const built = await Promise.all(store.map((s) => buildPayout(s, ctx)));
  let out = built.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  if (query.groupId) out = out.filter((p) => p.groupId === query.groupId);
  if (query.status) out = out.filter((p) => p.status === query.status);
  if (query.mode) out = out.filter((p) => p.disbursements.some((d) => d.mode === query.mode));
  return out;
}

/** A single payout for the processing screen or voucher. */
export async function getPayout(id: string): Promise<PayoutView> {
  if (!USE_MOCK_API) {
    return fetchPayoutDetail(id);
  }

  await delay(200);
  const ctx = await loadCtx();
  return buildPayout(findSeed(id), ctx);
}

/** Approve a payout (restricted to owner roles in the UI). */
export async function approvePayout(id: string, remarks: string, actor: string): Promise<PayoutView> {
  if (!USE_MOCK_API) {
    void actor; // the backend records the authenticated user, not a free-text actor name
    await http(`/payouts/${id}/approve`, { method: "POST", body: { remarks: remarks.trim() || undefined } });
    return fetchPayoutDetail(id);
  }

  await delay(450);
  const seed = findSeed(id);
  if (seed.approval) throw new ApiError("This payout is already approved.", 409, "ALREADY_APPROVED");
  if ((seed.disbursements?.length ?? 0) > 0) {
    throw new ApiError("This payout has already been disbursed.", 409, "ALREADY_DISBURSED");
  }
  const approval: PayoutApproval = { approvedBy: actor, approvedAt: new Date().toISOString(), remarks: remarks.trim() };
  store = store.map((s) => (s.id === id ? { ...s, approval } : s));
  const ctx = await loadCtx();
  return buildPayout(findSeed(id), ctx);
}

/** Add a manual deduction with a mandatory reason. */
export async function addManualDeduction(id: string, amount: number, reason: string, actor: string): Promise<PayoutView> {
  if (!USE_MOCK_API) {
    void actor;
    await http(`/payouts/${id}/deductions`, { method: "POST", body: { amount, reason } });
    return fetchPayoutDetail(id);
  }

  await delay(400);
  const seed = findSeed(id);
  if (seed.reversedAt) throw new ApiError("A reversed payout cannot be changed.", 409, "PAYOUT_REVERSED");
  if (amount <= 0) throw new ApiError("The deduction amount must be greater than zero.", 400, "INVALID_AMOUNT");
  if (!reason.trim()) throw new ApiError("A reason is required for the deduction.", 400, "REASON_REQUIRED");
  const deduction: PayoutManualDeduction = {
    id: `ded-${Date.now()}`,
    amount: Math.round(amount * 100) / 100,
    reason: reason.trim(),
    addedBy: actor,
    addedAt: new Date().toISOString(),
  };
  store = store.map((s) => (s.id === id ? { ...s, manualDeductions: [...(s.manualDeductions ?? []), deduction] } : s));
  const ctx = await loadCtx();
  return buildPayout(findSeed(id), ctx);
}

/** Record a disbursement against an approved payout. Requires prior approval. */
export async function recordDisbursement(id: string, input: RecordDisbursementInput): Promise<PayoutView> {
  if (!USE_MOCK_API) {
    const key = `disburse-${id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    await http(`/payouts/${id}/disburse`, {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: {
        amount: input.amount,
        mode: PAYMENT_MODE_TO_BACKEND[input.mode] ?? "CASH",
        referenceNo: input.reference.trim() || undefined,
        paidOn: toDateOnlyString(input.paidOn || todayDateOnly()),
      },
    });
    return fetchPayoutDetail(id);
  }

  await delay(500);
  const seed = findSeed(id);
  if (seed.reversedAt) throw new ApiError("A reversed payout cannot be disbursed.", 409, "PAYOUT_REVERSED");
  if (!seed.approval) throw new ApiError("This payout must be approved before it can be disbursed.", 409, "NOT_APPROVED");
  if (!Number.isFinite(input.amount) || input.amount <= 0) {
    throw new ApiError("The amount being paid must be greater than zero.", 400, "INVALID_AMOUNT");
  }
  if (input.mode !== "cash" && !input.reference.trim()) {
    throw new ApiError("A reference number is required for this payment mode.", 400, "REFERENCE_REQUIRED");
  }
  if (input.paidOn && new Date(input.paidOn).getTime() > Date.now()) {
    throw new ApiError("Disbursement cannot be dated in the future.", 409, "FUTURE_DATE");
  }
  const ctx = await loadCtx();
  const view = await buildPayout(seed, ctx);
  if (input.amount > view.balanceOwed + 0.01) {
    throw new ApiError(`The amount exceeds the net payable of ${view.netPayable.toLocaleString("en-IN")}.`, 409, "AMOUNT_EXCEEDS");
  }
  const disbursement: PayoutDisbursement = {
    id: `pay-${Date.now()}`,
    amount: Math.round(input.amount * 100) / 100,
    mode: input.mode,
    reference: input.reference.trim(),
    paidOn: input.paidOn || new Date().toISOString(),
    remarks: input.remarks.trim(),
    disbursedBy: input.actor,
    recordedAt: new Date().toISOString(),
  };
  store = store.map((s) => (s.id === id ? { ...s, disbursements: [...(s.disbursements ?? []), disbursement] } : s));
  return buildPayout(findSeed(id), ctx);
}

/** Reverse a disbursement/payout — records are never deleted, only reversed. */
export async function reversePayout(id: string, reason: string, actor: string): Promise<PayoutView> {
  if (!USE_MOCK_API) {
    void actor;
    await http(`/payouts/${id}/reverse`, { method: "POST", body: { reason } });
    return fetchPayoutDetail(id);
  }

  await delay(450);
  const seed = findSeed(id);
  if (!reason.trim()) throw new ApiError("A reversal reason is required.", 400, "REASON_REQUIRED");
  store = store.map((s) =>
    s.id === id
      ? {
          ...s,
          disbursements: [],
          approval: undefined,
          reversedAt: new Date().toISOString(),
          reversedBy: actor,
          reversalReason: reason.trim(),
        }
      : s
  );
  const ctx = await loadCtx();
  return buildPayout(findSeed(id), ctx);
}

/** Re-open a reversed payout back into pending approval for review and re-disbursement. */
export async function reopenPayout(id: string): Promise<PayoutView> {
  if (!USE_MOCK_API) {
    await http(`/payouts/${id}/reopen`, { method: "POST" });
    return fetchPayoutDetail(id);
  }

  await delay(350);
  store = store.map((s) =>
    s.id === id
      ? {
          ...s,
          reversedAt: undefined,
          approval: undefined,
        }
      : s
  );
  const ctx = await loadCtx();
  return buildPayout(findSeed(id), ctx);
}

/** Voucher data for print — includes server-generated amount in words in real-API mode. */
export async function getVoucher(id: string): Promise<PayoutView & { amountInWords?: string }> {
  if (!USE_MOCK_API) {
    const voucher = await http<BackendVoucher>(`/payouts/${id}/voucher`, { method: "GET" });
    const detail = await fetchPayoutDetail(id);
    return { ...detail, amountInWords: voucher.amountInWords };
  }
  return getPayout(id);
}

export const payoutService = {
  listPayouts,
  getPayout,
  approvePayout,
  addManualDeduction,
  recordDisbursement,
  reversePayout,
  reopenPayout,
  getVoucher,
};

export type { Member, KametiGroup };
